// hooks/usePageTracking.ts - React hook for page usage tracking
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import PageUsageService from '../utils/pageUsageService.ts';

interface UsePageTrackingOptions {
  trackOnMount?: boolean;
  trackOnUnmount?: boolean;
  pageTitle?: string;
  category?: string;
}

export const usePageTracking = (options: UsePageTrackingOptions = {}) => {
  const location = useLocation();
  const startTimeRef = useRef<number>(Date.now());
  const trackedRef = useRef<boolean>(false);
  const lastTrackedPath = useRef<string>('');
  
  const {
    trackOnMount = true,
    trackOnUnmount = true,
    pageTitle,
    category
  } = options;

  const pagePath = location.pathname;
  const detectedCategory = category || PageUsageService.detectCategory(pagePath);
  const displayTitle = pageTitle || PageUsageService.getPageDisplayName(pagePath);

  useEffect(() => {
    // Reset timing and tracking state when path changes
    startTimeRef.current = Date.now();
    
    // Only track if this is a new page or we haven't tracked this path yet
    if (trackOnMount && (pagePath !== lastTrackedPath.current || !trackedRef.current)) {
      // Track page visit on mount
      PageUsageService.trackPageVisit({
        pagePath,
        pageTitle: displayTitle,
        timeSpent: 0,
        category: detectedCategory
      });
      trackedRef.current = true;
      lastTrackedPath.current = pagePath;
    }

    return () => {
      if (trackOnUnmount && trackedRef.current && pagePath === lastTrackedPath.current) {
        // Track time spent when leaving the page
        const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
        
        // Only track unmount if we spent meaningful time (>1 second)
        if (timeSpent > 1) {
          PageUsageService.trackPageVisit({
            pagePath,
            pageTitle: displayTitle,
            timeSpent,
            category: detectedCategory
          });
        }
        trackedRef.current = false;
      }
    };
  }, [pagePath, displayTitle, detectedCategory, trackOnMount, trackOnUnmount]);

  // Manual tracking function
  const trackVisit = (customTimeSpent?: number) => {
    const timeSpent = customTimeSpent ?? Math.round((Date.now() - startTimeRef.current) / 1000);
    return PageUsageService.trackPageVisit({
      pagePath,
      pageTitle: displayTitle,
      timeSpent,
      category: detectedCategory
    });
  };

  return {
    trackVisit,
    pagePath,
    pageTitle: displayTitle,
    category: detectedCategory,
    timeSpent: () => Math.round((Date.now() - startTimeRef.current) / 1000)
  };
};
